/**
 * Fexr Wallet — custom RainbowKit v2 wallet
 *
 * When the user selects "Fexr Wallet" in the RainbowKit modal:
 *  - On mobile: opens the Fexr app via the fexr://wc deep-link
 *  - On desktop: shows a WalletConnect QR code the user scans with the Fexr
 *    wallet's built-in scanner (scan_screen.dart already handles wc: URIs)
 *
 * Matches RainbowKit v2's `CreateWalletFn` signature — RainbowKit calls this
 * function internally, passing `{ projectId, ... }` from `getDefaultConfig`.
 */

import type { Wallet } from "@rainbow-me/rainbowkit";
import { walletConnect } from "wagmi/connectors";

/** Vault participation (join, stake, polls) happens only in the Fexr app. */
export const FEXR_APP_URL = "https://app.fexr.io";

export const FEXR_WALLET_STORE = {
  ios: "https://apps.apple.com/app/fexr-wallet/id1631128786",
  android: "https://play.google.com/store/apps/details?id=com.getfexr.core",
} as const;

/** The wallet address last signed in with Fexr Wallet on this browser. */
export const FEXR_WALLET_ADDRESS_KEY = "fexr_wallet_login_address";

/** Same-phone handoff for a Fexr Wallet login request (wallet opens it). */
export function fexrWalletLoginLink(id: string, k: string): string {
  return `fexr://wallet-login?id=${encodeURIComponent(id)}&k=${encodeURIComponent(k)}`;
}

export interface FexrWalletOptions {
  projectId: string;
}

export function fexrMobileWallet({ projectId }: FexrWalletOptions): Wallet {
  return {
    id: "fexr-mobile",
    name: "Fexr Wallet",
    iconUrl: "https://app.fexr.io/icons/pwa-192.png",
    iconBackground: "#0A2535",

    downloadUrls: {
      ...FEXR_WALLET_STORE,
      mobile: "https://app.fexr.io",
      qrCode: "https://app.fexr.io",
    },

    mobile: {
      /**
       * Deep-link the Fexr app directly on iOS/Android.
       * The wallet's scan_screen already handles wc: URIs — this just
       * wraps it in the fexr:// scheme so the OS routes to the right app.
       */
      getUri: (uri: string) => `fexr://wc?uri=${encodeURIComponent(uri)}`,
    },

    qrCode: {
      /**
       * For desktop: show the raw WalletConnect URI as a QR code.
       * User scans it with Fexr Wallet's scanner.
       */
      getUri: (uri: string) => uri,
      instructions: {
        learnMoreUrl: "https://app.fexr.io",
        steps: [
          {
            description: "Open Fexr Wallet on your phone",
            step: "install",
            title: "Open Fexr Wallet",
          },
          {
            description:
              "Tap the scan icon in the top-right of the Fexr Wallet home screen",
            step: "scan",
            title: "Tap the QR scanner",
          },
          {
            description:
              "Point your camera at the QR code shown here to connect",
            step: "connect",
            title: "Scan this QR code",
          },
        ],
      },
    },

    /**
     * `createConnector` must return a wagmi `CreateConnectorFn` directly —
     * NOT `{ connector: ... }` (that was the v1 shape).
     */
    createConnector: (_walletDetails) =>
      walletConnect({
        projectId,
        showQrModal: false,
        metadata: {
          name: "Fexr Wallet",
          description:
            "Fexr Wallet — a self-custody wallet for trading and on-chain activity",
          url: "https://app.fexr.io",
          icons: ["https://app.fexr.io/icons/pwa-192.png"],
        },
      }),
  };
}
